import classNames from 'classnames';
import Column from 'components/utilities/column/column';
import Container from 'components/utilities/container/container';
import ModuleWrapper from 'components/utilities/module-wrapper/module-wrapper';
import Row from 'components/utilities/row/row';
import { type FC, type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './tab-component.scss';
import { ActivitiesTab } from 'app/pages/activities/tab';
import { FavouritesTab } from 'app/pages/favorites/tab';
import { MatchTab } from 'app/pages/match/tab';
import { MySalesTab } from 'app/pages/my-sales/tab';
import { useMixPanel } from 'hooks/useMixPanel';

export interface TabComponentProps {
  activeTab: string;
  tabContent: { item: ReactNode; key: string }[];
  tabNav: { isDisabled?: boolean; item: ReactNode; key: string }[];
}

const TabComponent: FC<TabComponentProps> = (props) => {
  const { activeTab, tabContent, tabNav } = props;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [visibleTab, setVisibleTab] = useState(activeTab);
  const [highlightedTab, setHighlightedTab] = useState(activeTab);
  const { track } = useMixPanel();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tabFromParams = searchParams.get('tab');
    if (tabFromParams) {
      setVisibleTab(tabFromParams);
      setHighlightedTab(tabFromParams);
    }
  }, [searchParams]);

  useEffect(() => {
    document.querySelector('.main-navigation')?.classList.add('no-shadow');

    return () => {
      document.querySelector('.main-navigation')?.classList.remove('no-shadow');
    };
  }, []);

  const handleOnClick = (key: string) => {
    setVisibleTab(key);
    navigate({
      search: '?tab=' + key,
    });

    switch (key) {
      case ActivitiesTab.ACTIVITIES:
        track('Activities_Navigate', { activitySection: 'FUTURE-ACTIVITIES' });
        break;

      case ActivitiesTab.PAST_ACTIVITIES:
        track('Activities_Navigate', { activitySection: 'PREVIOUS-ACTIVITIES' });
        break;

      case FavouritesTab.FAVORITES:
        track('Favorites_Navigate', { brokerSection: 'BROKER' });
        break;

      case FavouritesTab.FAVORITES_OTHER_BROKER:
        track('Favorites_Navigate', { brokerSection: 'OTHER-BROKER' });
        break;

      case MatchTab.MATCHES:
        track('Matches_Navigate', { matchSection: 'MATCHES' });
        break;

      case MatchTab.SEARCH_PROFILES:
        track('Matches_Navigate', { matchSection: 'SEARCH-AGENT' });
        break;

      case MySalesTab.ACTIVE:
        track('Mysales_Navigate', { salesSection: 'ACTIVE-SALES' });
        break;

      case MySalesTab.FINISHED:
        track('Mysales_Navigate', { salesSection: 'ENDED-SALES' });
        break;

      default:
        break;
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = tabNav.findIndex((tab) => tab.key === highlightedTab);

    if (event.key === 'ArrowLeft' && currentIndex > 0) {
      setHighlightedTab(tabNav[currentIndex - 1].key);
    } else if (event.key === 'ArrowRight' && currentIndex < tabNav.length - 1) {
      setHighlightedTab(tabNav[currentIndex + 1].key);
    }
  };

  useEffect(() => {
    if (containerRef.current?.matches(':focus-within')) {
      document.getElementById('tab-nav-id-' + highlightedTab)?.focus();
    }
  }, [highlightedTab]);

  return (
    <div className="tab-component">
      <ModuleWrapper backgroundColor="white">
        <Container ref={containerRef} paddingBottomReset paddingTopReset>
          <Row>
            <Column>
              <div className="tab-component__nav">
                {tabNav.map((item) => (
                  <button
                    key={item.key}
                    aria-controls={`tab-section-${item.key}`}
                    className={classNames('tab-component__nav-item', {
                      'is-active': visibleTab === item.key,
                      'is-disabled': item.isDisabled,
                      'is-highlighted': highlightedTab === item.key,
                    })}
                    disabled={item.isDisabled}
                    tabIndex={highlightedTab === item.key ? 0 : -1}
                    id={`tab-nav-id-${item.key}`}
                    onClick={() => handleOnClick(item.key)}
                    onKeyDown={handleKeyDown}
                    type="button"
                  >
                    {item.item}
                  </button>
                ))}
              </div>
            </Column>
          </Row>
        </Container>
      </ModuleWrapper>
      <div className="tab-component__content">
        {tabContent.map((component) => {
          if (component.key !== visibleTab) return null;
          return (
            <div
              key={component.key}
              className={`tab-component__content-item ${visibleTab === component.key && 'is-active'}`}
              id={`tab-section-${component.key}`}
            >
              {component.item}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TabComponent;
